import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Canceler } from "axios";
import { useAppTable, useFormContainerState, useTrans } from "../../../hooks";
import {
    Button,
    VStack,
    HStack,
    Divider,
    Center,
    Text,
    Hide,
    useToast,
    useDisclosure,
    Box,
} from "@chakra-ui/react";
import { updatePageIndex } from "ka-table/actionCreators";
import { AppSearch } from "../../../containers";
import {
    AppAlertDelete,
    AppFilterButton,
    AppIcon,
    AppPageContent,
    AppPageHeader,
} from "../../../components";
import { CompanyList as ListComponent } from "./CompanyList";
import { CompanyForm as FormComponent } from "./CompanyForm";
import { CompanyFilter as FilterComponent } from "./CompanyFilter";
import { Company as Entity, CompanyApi as EntityApi } from "../../../apis";
import { APPC, cPath } from "../../../config";

export const CompanyPage = () => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const navigate = useNavigate();
    const formContainerState = useFormContainerState();
    const deleteAlertState = useDisclosure();

    // state & const
    const [search, setSearch] = useState("");
    const [filterData, setFilterData] = useState<any>({});
    const [loadingList, setLoadingList] = useState(true);
    const [list, setList] = useState<Entity[]>([]);
    const [actionSelectId, setActionSelectId] = useState(0);
    const [isDetailPage, setIsDetailPage] = useState(false);

    const cancelTokenSourcesRef = useRef<Canceler[]>([]);

    const {
        table,
        pageIndex,
        pagesCount,
        pageSize,
        sortColumn,
        sortOrder,
        setPagesCount,
    } = useAppTable("name");

    const paging = {
        enabled: true,
        pageSizes: APPC.PAGE_SIZES,
        pageSize,
        pageIndex,
        pagesCount,
    };

    // handler
    const fetchListData = async () => {
        setLoadingList(true);
        const params = {
            itemsPerPage: pageSize,
            ...filterData,
        };
        params[`order[${sortColumn}]`] = sortOrder;
        if (search) {
            params["company_search"] = search;
        }

        await EntityApi.getCollection<Entity>(pageIndex + 1, params, (c) => {
            cancelTokenSourcesRef.current.push(c);
        })
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setPagesCount(Math.ceil(response.totalItems / pageSize));
                    setList(response.items);
                }
            })
            .finally(() => setLoadingList(false));
    };

    const actionClickHandler = (key: string, data?: Entity) => {
        if (!data) {
            return;
        }
        switch (key) {
            case "edit":
                setIsDetailPage(false);
                formContainerState.open(data?.id);
                break;
            case "delete":
                setActionSelectId(data?.id);
                deleteAlertState.onOpen();
                break;
            case "serviceGroup":
                navigate(
                    cPath("ADMIN.SERVICEGROUP_PAGE", {
                        id: data?.id,
                    })
                );
                break;
            case "companyDocument":
                navigate(
                    cPath("ADMIN.COMPANYDOCUMENT_PAGE", {
                        id: data?.id,
                    })
                );
                break;
            case "detail":
                setIsDetailPage(true);
                formContainerState.open(data?.id);
                break;
        }
    };

    const deleteHandler = async () => {
        if (actionSelectId < 1) {
            return;
        }
        setLoadingList(true);
        await EntityApi.deleteItem(actionSelectId)
            .then(({ errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else {
                    fetchListData();
                }
            })
            .finally(() => setLoadingList(false));
    };

    // useEffect
    useEffect(() => {
        if (!formContainerState.isOpen) {
            fetchListData();
        }
    }, [
        pageIndex,
        pageSize,
        sortColumn,
        sortOrder,
        search,
        filterData,
        formContainerState.isOpen,
    ]);

    useEffect(() => {
        if (pageIndex > 0) {
            table.dispatch(updatePageIndex(0));
        }
    }, [filterData]);

    return (
        <>
            <AppPageHeader title={t("padm.CompanyPage:text.pageTitle")}>
                <HStack gap={4} py={{ base: 3.5, sm: 4, md: "1.125rem" }}>
                    <AppSearch
                        onSearch={(value) => {
                            setSearch(value);
                            if (pageIndex > 0) {
                                table.dispatch(updatePageIndex(0));
                            }
                        }}
                    />
                    <FilterComponent
                        filterData={filterData}
                        setFilterData={setFilterData}
                    >
                        <Box role="button">
                            <AppFilterButton filterData={filterData} />
                        </Box>
                    </FilterComponent>
                    <Center height="2.1875rem">
                        <Divider
                            orientation="vertical"
                            borderColor={"var(--chakra-colors-greyT80)"}
                        />
                    </Center>
                    <Button
                        variant={"primary"}
                        onClick={() => {
                            setIsDetailPage(false);
                            formContainerState.open(0);
                        }}
                    >
                        <AppIcon name="ics-plus-circle" w="1rem" />
                        <Hide below="md">
                            <Text ml={2}>
                                {t("padm.CompanyPage:button.create")}
                            </Text>
                        </Hide>
                    </Button>
                </HStack>
            </AppPageHeader>
            <AppPageContent>
                <>
                    <VStack p={6}>
                        <ListComponent
                            table={table}
                            paging={paging}
                            list={list}
                            loadingList={loadingList}
                            onActionClick={actionClickHandler}
                        />
                        {deleteAlertState.isOpen && (
                            <AppAlertDelete
                                isOpenDelete={deleteAlertState.isOpen}
                                deleteHandler={() => deleteHandler()}
                                onCloseDelete={deleteAlertState.onClose}
                            />
                        )}
                    </VStack>
                    {formContainerState.isOpen && (
                        <FormComponent
                            formContainerState={formContainerState}
                            isDetailPage={isDetailPage}
                        />
                    )}
                </>
            </AppPageContent>
        </>
    );
};
